/**
 * The agents' cursors on the live view of a cloud computer. The X pointer alone cannot show where an agent works:
 * A mission clicks through Chromium's DevTools protocol (agent-browser, code actions), which never moves it, and several agents may work
 * side by side. So MachineDO tells every viewer of a desktop where each agent acts, on the socket
 * GET /api/machines/<id>/pointer?desktop=N:
 * - computer.* actions (the Claude computer-use loop, xdotool on desktop 0): MachineDO knows the coordinates;
 * - browser actions on any desktop: the Chromium extension container/pointer-extension reports the screen point of every
 *   trusted click, wheel and typing to http://192.0.2.40/pointer, which MachineDO intercepts (PointerGate) and
 *   attributes to the run bound on that desktop.
 * Positions are pixels of the 1280 × 800 screen. Nothing is sent while the owner holds the controls.
 * The live view keeps each agent's cursor where it last pointed and glides it to the next point; a scroll, typing or an
 * action without a point (AgentPointerHide, or the "scroll" and "type" actions) fades it out until it points again.
 */

/** Where the computer's Chromium extension reports (MachineDO intercepts it for its container). */
export const POINTER_GATE_ADDR = "192.0.2.40:80";

export const POINTER_ACTIONS = ["move", "click", "double_click", "right_click", "drag", "scroll", "type"] as const;
export type PointerAction = (typeof POINTER_ACTIONS)[number];

/** One agent acting at a point of a desktop's screen. */
export interface AgentPointerEvent {
  type: "pointer";
  /** Who acts: the run's id (a mission, a sub-agent), or "agent" for the agent itself outside of a run. */
  actor: string;
  desktop: number;
  x: number;
  y: number;
  action: PointerAction;
  /** computer: the computer-use loop (xdotool); browser: a click or typing reported by the page (agent-browser, code actions). */
  source: "computer" | "browser";
  at: number;
}

/** Why an agent's cursor fades out: an action without a point (a page opened…), or its run ended. */
export type PointerHideReason = "other" | "done";

/** An agent stops pointing: its cursor fades out on the live view until its next pointed action. */
export interface AgentPointerHide {
  type: "pointer_hide";
  actor: string;
  /** null: every desktop (the run ended, wherever it worked). */
  desktop: number | null;
  reason: PointerHideReason;
  at: number;
}

/** What the pointer socket carries (server → viewer only). */
export type PointerFrame = AgentPointerEvent | AgentPointerHide | { type: "hello"; desktop: number };

export const SCREEN_WIDTH = 1280;
export const SCREEN_HEIGHT = 800;
